/**
 * Emballagekompensation.
 *
 * Indsamleren henter affald for sine erhvervskunder. Kunden kan søge
 * kompensation for emballagedelen, men skal kunne dokumentere mængderne.
 * Siden samler indsamlingsdata fra DORA og udskriver rapporten, som
 * indsamleren sender til sit modtageanlæg.
 */
import { useEffect, useState } from 'react';
import { Alert, Badge, Box, Snackbar, Tab, Tabs, Typography } from '@mui/material';
import { api } from '../../services/api';
import type { EmballageStatus } from '../../types';
import EmballageReportPanel from './EmballageReportPanel';
import EmballageCustomersPanel from './EmballageCustomersPanel';
import EmballageProductsPanel from './EmballageProductsPanel';
import EmballageFacilitiesPanel from './EmballageFacilitiesPanel';

type Notice = { msg: string; error: boolean } | null;

export default function EmballagePage() {
  const [tab, setTab] = useState(0);
  const [status, setStatus] = useState<EmballageStatus | null>(null);
  const [notice, setNotice] = useState<Notice>(null);

  const refreshStatus = async () => {
    try { setStatus(await api.getEmballageStatus()); } catch { /* badges er pynt */ }
  };

  useEffect(() => { void refreshStatus(); }, []);

  const notify = (msg: string, error = false) => setNotice({ msg, error });

  // Rapporten kan ikke laves før begge dele er på plads, så manglen siges én
  // gang her i stedet for som en fejl efter et forgæves forsøg.
  const mangler: string[] = [];
  if (status && status.enrolledCount === 0) mangler.push('tilmeldte kunder');
  if (status && status.productCount === 0) mangler.push('varer med en fraktion');
  if (status && status.facilityCount === 0) mangler.push('et modtageanlæg');

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>Emballagekompensation</Typography>

      <Tabs value={tab} onChange={(_, v) => setTab(v as number)} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="Rapport" />
        <Tab label={<Badge color="primary" badgeContent={status?.enrolledCount || 0} showZero={false} sx={{ pr: 1.5 }}>Kunder</Badge>} />
        <Tab label={<Badge color="primary" badgeContent={status?.productCount || 0} showZero={false} sx={{ pr: 1.5 }}>Varer</Badge>} />
        <Tab label="Modtageanlæg" />
      </Tabs>

      {tab === 0 && mangler.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Mangler {mangler.join(', ')}.
        </Alert>
      )}

      {tab === 0 && <EmballageReportPanel onNotify={notify} />}
      {tab === 1 && <EmballageCustomersPanel onNotify={notify} onChanged={refreshStatus} />}
      {tab === 2 && <EmballageProductsPanel onNotify={notify} onChanged={refreshStatus} />}
      {tab === 3 && <EmballageFacilitiesPanel onNotify={notify} onChanged={refreshStatus} />}

      <Snackbar
        open={notice != null}
        autoHideDuration={5000}
        onClose={() => setNotice(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={notice?.error ? 'error' : 'success'} onClose={() => setNotice(null)}>
          {notice?.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
